import { forwardRef, type ReactNode, useEffect, useMemo } from 'react';
import { NotificationImportant } from '@mui/icons-material';
import { Avatar, Box, Chip, InputAdornment, Stack, type StackProps, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { type Control, type FieldPath, type UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InvoiceItemsFormPart } from '../../../../invoice-app/components/ConnectedInvoiceItemsFormPart';
import { FormDatePicker } from '../../../components/form/FormDatePicker';
import { FormInput, FormInputProps } from '../../../components/form/FormInput';
import { FormNumericInput, FormNumericInputProps } from '../../../components/form/FormNumericInput';
import { FormSelect, FormSelectProps } from '../../../components/form/FormSelect';
import { currencies } from '../../../utils/currencies';
import { ConnectedFormFieldGroup } from './connected/ConnectedFormFieldGroup';
import { CustomerFormPart } from './form-parts/CustomerFormPart';
import { VendorFormPart } from './form-parts/VendorFormPart';
import { inputSharedProps } from './shared';
import type { InvoiceFormType } from './types';

type InvoiceFormProps = {
  control: Control<InvoiceFormType>;
  setValue: UseFormSetValue<InvoiceFormType>;
  slots?: {
    customerSlot?: ReactNode;
    vendorSlot?: ReactNode;
    invoiceHeaderSlot?: ReactNode;
    vendorHeaderSlot?: ReactNode;
    customerHeaderSlot?: ReactNode;
  };
  readonly?: boolean;
  highlightAreas?: boolean;
  selectedCurrency?: string | null;
  formId?: string;
  allowInvoiceItems?: boolean;
} & StackProps<'form'>;

export const InvoiceForm = forwardRef<HTMLFormElement, InvoiceFormProps>((props, ref) => {
  const {
    control,
    slots,
    readonly,
    highlightAreas = true,
    selectedCurrency,
    formId = 'invoice-form',
    allowInvoiceItems = true,
    setValue,
    ...stackProps
  } = props;

  const { t } = useTranslation();

  const formInputSharedProps = useMemo(
    () =>
      ({
        ...inputSharedProps,
      }) satisfies Partial<FormSelectProps & FormInputProps>,
    [],
  );

  const CustomerSlot = useMemo(
    () =>
      slots?.customerSlot ?? (
        <CustomerFormPart inputProps={formInputSharedProps} readonly={readonly} control={control} />
      ),
    [control, formInputSharedProps, readonly, slots?.customerSlot],
  );
  const VendorSlot = useMemo(
    () =>
      slots?.vendorSlot ?? <VendorFormPart inputProps={formInputSharedProps} readonly={readonly} control={control} />,
    [control, formInputSharedProps, readonly, slots?.vendorSlot],
  );

  const currencyProps = useMemo(() => {
    const currency = currencies.find((curr) => curr.value.toLowerCase() === selectedCurrency?.toLowerCase());
    return {
      position: currency?.position ?? 'suffix',
      unicode: currency?.unicode ?? '',
    };
  }, [selectedCurrency]);

  return (
    <Stack
      {...stackProps}
      id={formId}
      component={'form'}
      ref={ref}
      autoComplete={'off'}
      spacing={{
        xs: 2,
        md: 6,
      }}
      sx={{ overflow: 'hidden' }}
    >
      {/*CUSTOMER AND Vendor SECTION*/}
      <Grid
        container
        spacing={{
          xs: 2,
          md: 6,
        }}
      >
        <Grid
          xs={12}
          container
          spacing={{
            xs: 2,
            md: 8,
          }}
        >
          <Grid xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant={'h4'}>{t('invoice')}</Typography>
              {slots?.invoiceHeaderSlot}
            </Box>
          </Grid>
          <Grid xs={6}>
            <FormInput
              {...formInputSharedProps}
              readonly={readonly}
              control={control}
              name={'number'}
              label={t('invoice.number') + '*'}
            />
          </Grid>
        </Grid>
        <Grid xs={12} md={6}>
          {/* Vendor */}
          <Box
            sx={
              highlightAreas
                ? {
                    pt: {
                      xs: 1,
                      md: 4,
                    },
                    pb: {
                      xs: 1,
                      md: 2,
                    },
                  }
                : null
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant={'h4'}>{t('vendor')}</Typography>
              {slots?.vendorHeaderSlot}
            </Box>
            {VendorSlot}
          </Box>
        </Grid>
        <Grid xs={12} md={6} pb={0}>
          {/* CUSTOMER */}
          <Box
            sx={
              highlightAreas
                ? {
                    border: 1,
                    borderRadius: 1,
                    borderStyle: 'dashed',
                    borderColor: 'primary.main',
                    position: 'relative',
                    pt: 4,
                    px: 2,
                    pb: 2,
                  }
                : undefined
            }
          >
            {highlightAreas ? (
              <Chip
                color={'primary'}
                icon={<NotificationImportant />}
                label={
                  <Typography variant={'body2'} fontWeight={'bold'}>
                    Údaje použijeme pro upomínání
                  </Typography>
                }
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  translate: '-50% -50%',
                }}
              />
            ) : null}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant={'h4'}>{t('customer')}</Typography>
              {slots?.customerHeaderSlot}
            </Box>
            {CustomerSlot}
          </Box>
        </Grid>
        <Grid xs={12} container>
          <Grid xs={6} paddingBottom={0}>
            <Typography variant={'h4'}>{t('paymentInfo')}</Typography>
          </Grid>
          <Grid
            xs={12}
            container
            spacing={{
              xs: 2,
              md: 8,
            }}
          >
            <Grid xs={12} md={6} container>
              <Grid xs={12}>
                <ConnectedFormFieldGroup
                  readonly={readonly}
                  label={'\xa0'}
                  formNames={[]}
                  sx={{
                    border: 0,
                    px: 0,
                  }}
                >
                  <Grid xs={12} container spacing={2}>
                    <Grid xs={12} md={6}>
                      <FormDatePicker
                        readonly={readonly}
                        textFieldProps={formInputSharedProps}
                        control={control}
                        name={'issueDate'}
                        label={t('issueDate') + '*'}
                      />
                    </Grid>
                    <Grid xs={12} md={6}>
                      <FormDatePicker
                        readonly={readonly}
                        textFieldProps={formInputSharedProps}
                        control={control}
                        name={'dueDate'}
                        label={t('dueDate') + '*'}
                      />
                    </Grid>
                    <Grid xs={12}>
                      <FormInput
                        {...formInputSharedProps}
                        control={control}
                        name={'variableSymbol'}
                        label={t('variableSymbol') + '*'}
                        readonly={readonly}
                      />
                    </Grid>
                  </Grid>
                </ConnectedFormFieldGroup>
              </Grid>
            </Grid>
            <Grid xs={12} md={6} container>
              <Grid xs={12}>
                <ConnectedFormFieldGroup
                  readonly={readonly}
                  required={true}
                  label={'Bankovní informace'}
                  helperText={t('IbanOrBankAccountAreRequired') as string}
                  formNames={['bankAccount', 'bankNumber', 'iban', 'swift']}
                >
                  <Grid xs={12} container spacing={2}>
                    <Grid xs={8}>
                      <FormInput
                        {...formInputSharedProps}
                        disableRequiredErrorIndication={true}
                        readonly={readonly}
                        control={control}
                        name={'bankAccount'}
                        label={t('bankAccount')}
                        InputProps={{
                          ...formInputSharedProps,
                          endAdornment: <InputAdornment position={'end'}>/</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid xs={4}>
                      <FormInput
                        {...formInputSharedProps}
                        disableRequiredErrorIndication={true}
                        readonly={readonly}
                        control={control}
                        name={'bankNumber'}
                        label={t('bankNumber')}
                      />
                    </Grid>
                    <Grid xs={12} md={8}>
                      <FormInput
                        {...formInputSharedProps}
                        disableRequiredErrorIndication={true}
                        readonly={readonly}
                        control={control}
                        name={'iban'}
                        label={t('iban')}
                      />
                    </Grid>
                    <Grid xs={12} md={4}>
                      <FormInput
                        {...formInputSharedProps}
                        disableRequiredErrorIndication={true}
                        readonly={readonly}
                        control={control}
                        name={'swift'}
                        label={t('swift')}
                      />
                    </Grid>
                  </Grid>
                </ConnectedFormFieldGroup>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {allowInvoiceItems && (
          <Grid xs={12} container>
            <Grid xs={12} paddingBottom={0}>
              <Typography variant={'h4'}>{'Položky faktury'}</Typography>
            </Grid>
            <Grid xs={12}>
              {
                <InvoiceItemsFormPart
                  readonly={readonly}
                  control={control}
                  setValue={setValue}
                  inputProps={formInputSharedProps}
                  allowTaxPayer={false}
                />
              }
            </Grid>
          </Grid>
        )}
        <Box
          sx={{
            height: '1px',
            borderTopWidth: 1,
            borderTopStyle: 'dashed',
            borderTopColor: highlightAreas ? 'primary.main' : 'divider',
            position: 'relative',
            width: '100%',
            mt: { xs: 4, md: 1 },
            mb: { xs: 2, md: 0 },
          }}
        >
          {highlightAreas ? (
            <Avatar
              sx={{
                position: 'absolute',
                top: 0,
                left: '50%',
                translate: '-50% -50%',
                bgcolor: 'primary.main',
              }}
            >
              <NotificationImportant />
            </Avatar>
          ) : null}
        </Box>
        <Grid xs={12} container>
          <Grid xs={12} container spacing={2}>
            <Grid xs={8} px={readonly ? 1 : 2}>
              <AutoFilledPriceSumInput
                setValue={setValue}
                baseName={'items'}
                readonly={readonly}
                variant={'standard'}
                control={control}
                name={'amount'}
                label={t('invoice.amount') + '*'}
                helperText={readonly ? '' : t('helperText.amount')}
                numericInputProps={{
                  [currencyProps.position]: ` ${currencyProps?.unicode} `,
                }}
                InputProps={formInputSharedProps}
              />
            </Grid>
            <Grid xs={4} px={readonly ? 1 : 2}>
              <FormSelect
                selectFieldProps={formInputSharedProps}
                readonly={readonly}
                variant={'standard'}
                control={control}
                name={'currency'}
                label={t('currency') + '*'}
                options={currencies}
              />
            </Grid>
            <Grid xs={readonly ? 8 : 12}>
              <FormNumericInput
                readonly={readonly}
                variant={readonly ? 'standard' : 'filled'}
                control={control}
                name={'amountDue'}
                label={t('amountDue') + '*'}
                helperText={readonly ? '' : t('helperText.amountDue')}
                numericInputProps={{
                  [currencyProps.position]: ` ${currencyProps?.unicode} `,
                }}
                InputProps={formInputSharedProps}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Stack>
  );
});

type AutoFilledPriceSumInputProps = {
  control: Control<InvoiceFormType>;
  setValue: UseFormSetValue<InvoiceFormType>;
  baseName: FieldPath<InvoiceFormType>;
} & FormNumericInputProps;

const AutoFilledPriceSumInput = ({ control, setValue, baseName, ...rest }: AutoFilledPriceSumInputProps) => {
  const results = useWatch({
    control,
    name: baseName,
    defaultValue: [],
  });

  const sum = useMemo(() => {
    if (!Array.isArray(results) || results.length === 0) {
      return undefined;
    }

    return results.reduce((acc, item) => acc + Number(item.priceSum ?? 0), 0);
  }, [results]);

  useEffect(() => {
    if (sum !== undefined) {
      setValue(rest.name as FieldPath<InvoiceFormType>, sum);
    }
  }, [sum, setValue, rest.name]);

  return <FormNumericInput control={control} {...rest} />;
};

InvoiceForm.displayName = 'InvoiceForm';
